/** Onboarding status from BFF — chat gate, CTAs, and composer placeholder copy. */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { POSTHOG } from "@/constants";
import { IS_LOCAL_DEV } from "@/config/localDev";
import { posthog } from "@/config/posthog.config";
import { useAuth } from "@/contexts/AuthContext";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useOnboardingFeatureEnabled } from "@/hooks/useOnboardingFeatureEnabled";
import {
  useOnboardingSourcesQuery,
  useOnboardingStatusQuery,
  useOnboardingWarehouseQuery,
} from "@/hooks/useOnboardingQueries";
import {
  countOnboardingSetupStepsComplete,
  ONBOARDING_SETUP_STEP_COUNT,
} from "@/helpers/onboardingSetupProgress";
import { ONBOARDING_COPY } from "@/constants/onboardingCopy";
import { userCanRunOnboardingSetup } from "@/helpers/onboardingSetupAccess";
import type { OnboardingStatusResponse } from "@/types/onboarding.types";

export interface OnboardingContextValue {
  chatGateFlagLoaded: boolean;
  chatGateEnabled: boolean;
  status?: OnboardingStatusResponse;
  statusLoading: boolean;
  /** When true, show setup banner + gated empty-state copy (wizard incomplete). */
  showCompleteOnboarding: boolean;
  /** When false, banner is informational only (non-admin `setupIncompleteMessage`). */
  showCompleteOnboardingCta: boolean;
  /** Wizard done; waiting on readiness — show post-setup status in chat (D14). */
  showPostWizardSetupStatus: boolean;
  /** Skip legacy Pulse warehouse gate so chat shell can render (D12 extension). */
  skipLegacyWarehouseGate: boolean;
  /** Status in flight — avoid flashing WarehouseScreen before post-wizard state resolves. */
  deferLegacyWarehouseGate: boolean;
  composerDisabledByOnboarding: boolean;
  /** Wizard / chat gate only — does not include subscription composer block. */
  shellNavLockedByOnboarding: boolean;
  composerPlaceholder: string | undefined;
  composerFootnote: string | undefined;
  setupStepsCompleted: number;
  setupStepsTotal: number;
  setupBannerSubtitle: string;
}

const OnboardingContext = createContext<OnboardingContextValue | undefined>(
  undefined,
);

export function useOnboardingContext(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error("useOnboardingContext must be used within OnboardingProvider");
  }
  return ctx;
}

interface OnboardingProviderProps {
  children: ReactNode;
}

function resolveComposerPlaceholder(
  showPostWizardSetupStatus: boolean,
  composerDisabledByOnboarding: boolean,
): string | undefined {
  if (showPostWizardSetupStatus) {
    return ONBOARDING_COPY.SETUP_STATUS_COMPOSER_PLACEHOLDER;
  }
  if (composerDisabledByOnboarding) {
    return ONBOARDING_COPY.COMPOSER_PLACEHOLDER;
  }
  return undefined;
}

function resolveSetupBannerSubtitle(
  status: OnboardingStatusResponse | undefined,
  canRunOnboardingSetup: boolean,
  handoffEnabled: boolean,
  setupStepsCompleted: number,
): string {
  if (status?.setupIncompleteMessage && !canRunOnboardingSetup) {
    return status.setupIncompleteMessage;
  }
  if (handoffEnabled) {
    return ONBOARDING_COPY.SETUP_BANNER_SUBTITLE;
  }
  return ONBOARDING_COPY.SETUP_PROGRESS_SUBTITLE(
    setupStepsCompleted,
    ONBOARDING_SETUP_STEP_COUNT,
  );
}

/**
 * Provide onboarding status, chat-gate state, and setup progress to the app shell.
 */
export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const { userDetails, currentUser, permissions: permissionBuckets } = useAuth();
  const {
    enabled: onboardingActive,
    loaded: onboardingFlagsLoaded,
    wizardEnabled,
    chatGateEnabled,
  } = useOnboardingFeatureEnabled();
  const { enabled: handoffEnabled } = useFeatureFlag(
    POSTHOG.FEATURE_FLAGS.ONBOARDING_SOURCE_HANDOFF,
  );

  const companyId = userDetails?.company?.companyId as number | undefined;
  const statusQueryEnabled =
    !IS_LOCAL_DEV &&
    Boolean(userDetails?.userId) &&
    Boolean(currentUser) &&
    onboardingFlagsLoaded &&
    onboardingActive;

  const statusQuery = useOnboardingStatusQuery(statusQueryEnabled, companyId, chatGateEnabled);
  const status = statusQuery.data;
  // Gate on the last good status, not `isSuccess`: in TanStack v5 a failed background
  // refetch flips status to `error` but keeps `data`, and one failed focus refetch must
  // not unlock the composer or swap the shell back to the legacy warehouse screen.
  const statusResolved = statusQueryEnabled && status != null;
  const showCompleteOnboarding = statusResolved && status.wizardCompleted === false && wizardEnabled;
  const canRunOnboardingSetup = userCanRunOnboardingSetup(permissionBuckets);
  const showCompleteOnboardingCta = showCompleteOnboarding && canRunOnboardingSetup;
  // When wizard onboarding is incomplete, chat composer and side panel navigation
  // are disabled until onboarding is complete for users who can complete it (admins)
  // while the chat gate flag (`onboarding-chat-gate-enabled`) is active.
  // Non-admins cannot run onboarding setup, so they must not be locked out.
  // When the wizard is done, CS readiness gate (`onboarding-chat-gate-enabled`) controls locking chat and nav.
  const awaitingReadiness = statusResolved && status.wizardCompleted && !status.chatAllowed;
  const blockChatFromOnboarding = statusResolved && chatGateEnabled && !status.chatAllowed;
  const showPostWizardSetupStatus = awaitingReadiness && chatGateEnabled;
  const deferLegacyWarehouseGate = statusQueryEnabled && statusQuery.isLoading;
  const onboardingClearsLegacyWarehouseGate = statusResolved && status.chatAllowed === true;
  const lockedByIncompleteWizard = chatGateEnabled && showCompleteOnboardingCta;
  const composerDisabledByOnboarding = lockedByIncompleteWizard || blockChatFromOnboarding;
  const shellNavLockedByOnboarding = lockedByIncompleteWizard || blockChatFromOnboarding;

  // Once each time the lock turns on (not per render): how often onboarding blocks chat.
  useEffect(() => {
    if (!composerDisabledByOnboarding) return;
    posthog?.capture(POSTHOG.EVENTS.CHAT_COMPOSER_GATE_SHOWN, {
      reason: showPostWizardSetupStatus ? "awaiting_readiness" : "wizard_incomplete",
    });
  }, [composerDisabledByOnboarding, showPostWizardSetupStatus]);

  const detailQueriesEnabled =
    (showCompleteOnboarding || blockChatFromOnboarding) && canRunOnboardingSetup;
  const warehouseQuery = useOnboardingWarehouseQuery(detailQueriesEnabled, companyId);
  const sourcesQuery = useOnboardingSourcesQuery(detailQueriesEnabled, companyId);
  const setupStepsCompleted = countOnboardingSetupStepsComplete(
    warehouseQuery.data,
    sourcesQuery.data,
    status?.wizardCompleted,
  );
  const setupBannerSubtitle = useMemo(
    () =>
      resolveSetupBannerSubtitle(
        status,
        canRunOnboardingSetup,
        handoffEnabled,
        setupStepsCompleted,
      ),
    [canRunOnboardingSetup, handoffEnabled, setupStepsCompleted, status],
  );

  const value = useMemo<OnboardingContextValue>(
    () => ({
      chatGateFlagLoaded: onboardingFlagsLoaded,
      chatGateEnabled,
      status,
      statusLoading: statusQueryEnabled && statusQuery.isLoading,
      showCompleteOnboarding,
      showCompleteOnboardingCta,
      showPostWizardSetupStatus,
      skipLegacyWarehouseGate:
        IS_LOCAL_DEV ||
        awaitingReadiness ||
        showCompleteOnboarding ||
        onboardingClearsLegacyWarehouseGate,
      deferLegacyWarehouseGate,
      composerDisabledByOnboarding,
      shellNavLockedByOnboarding,
      composerPlaceholder: resolveComposerPlaceholder(
        showPostWizardSetupStatus,
        composerDisabledByOnboarding,
      ),
      composerFootnote: showPostWizardSetupStatus
        ? ONBOARDING_COPY.SETUP_STATUS_COMPOSER_FOOTNOTE
        : undefined,
      setupStepsCompleted,
      setupStepsTotal: ONBOARDING_SETUP_STEP_COUNT,
      setupBannerSubtitle,
    }),
    [
      awaitingReadiness,
      chatGateEnabled,
      composerDisabledByOnboarding,
      shellNavLockedByOnboarding,
      deferLegacyWarehouseGate,
      onboardingClearsLegacyWarehouseGate,
      onboardingFlagsLoaded,
      setupBannerSubtitle,
      setupStepsCompleted,
      showCompleteOnboarding,
      showCompleteOnboardingCta,
      showPostWizardSetupStatus,
      status,
      statusQuery.isLoading,
      statusQueryEnabled,
    ],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}
